import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

/**
 * Available MuiTypography variants (fontSize / lineHeight):
 *
 * Body:
 * - body/xl: 18px / 24px
 * - body/lg: 16px / 24px
 * - body/md: 14px / 18px
 * - body/sm: 12px / 14px
 *
 * Button:
 * - button/lg: 16px / 24px
 * - button/md: 14px / 18px
 * - button/sm: 12px / 14px
 *
 * Label:
 * - label/lg: 14px / 14px
 * - label/md: 12px / 12px
 * - label/sm: 10px / 12px
 *
 * System:
 * - system: 10px / 12px
 */

export interface TextProps
  extends Omit<TypographyProps, 'variant' | keyof PounceStyleProps>, PounceStyleProps {
  /** The variant of the font */
  variant?: Exclude<TypographyProps['variant'], 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>;

  /** Override the component type */
  as?: React.ElementType;
}

/**
 * Responsive typographic component. Whenever you want to add some text that doesn't constitute a
 * heading or a title, this component is what you need.
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  { variant = 'body/md', as, sx, children, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const combinedSx = {
    ...pounceStyles,
    ...(sx || {}),
  };

  return (
    <Typography ref={ref} variant={variant} component={as || 'p'} sx={combinedSx} {...otherProps}>
      {children}
    </Typography>
  );
});
